/**
 * DomainUsersTab — 收件域右侧"邮箱账号"Tab
 * 展示选中收件域下的邮箱账号：新建/编辑/删除/多选/分页/详情（新建自动带入选中域名）。
 */
import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  Text,
  Input,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Switch,
} from '@fluentui/react-components'
import { AddRegular, EditRegular } from '@fluentui/react-icons'
import { usersApi } from '@/api/users'
import type { User, Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import { formatQuota, formatDate } from '@/lib/format'

const PAGE_SIZE = 10

interface UserPayload {
  domain_id: number
  username: string
  password: string
  display_name?: string
  quota_bytes?: number
}

interface UserFormState {
  username: string
  password: string
  display_name: string
  quota_bytes: string
}

const EMPTY_FORM: UserFormState = { username: '', password: '', display_name: '', quota_bytes: '' }

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' },
  formGridItem: { minWidth: 0 },
  colSpan2: { gridColumn: '1 / -1' },
  dialogSurface: { width: '65vw', minWidth: '600px', maxWidth: '1000px' },
})

export default function DomainUsersTab({ domain }: { domain: Domain }) {
  const styles = useStyles()
  const queryClient = useQueryClient()

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  })

  const domainUsers = useMemo(
    () => (users ?? []).filter((u) => u.domain === domain.name),
    [users, domain.name],
  )

  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return domainUsers
    return domainUsers.filter(
      (u) => u.username.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [domainUsers, query])

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filteredUsers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  useEffect(() => {
    setQuery('')
    setPage(1)
  }, [domain.id])

  // 新建
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM)
  const openCreate = () => {
    setForm(EMPTY_FORM)
    setCreateOpen(true)
  }

  const createMutation = useMutation({
    mutationFn: async (data: UserPayload) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('创建成功', '用户已创建')
      setCreateOpen(false)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '创建失败，请稍后重试'
      toast.error('创建失败', msg)
    },
  })

  const handleSubmit = () => {
    const username = form.username.trim()
    if (!username) {
      toast.error('请填写用户名')
      return
    }
    if (!form.password) {
      toast.error('请填写密码')
      return
    }
    createMutation.mutate({
      domain_id: domain.id,
      username,
      password: form.password,
      display_name: form.display_name.trim() || undefined,
      quota_bytes: form.quota_bytes.trim() ? Number(form.quota_bytes) : undefined,
    })
  }

  // 编辑
  const [editOpen, setEditOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editDisplayName, setEditDisplayName] = useState('')
  const [editQuota, setEditQuota] = useState('')
  const [editActive, setEditActive] = useState(true)

  const openEdit = (u: User) => {
    setEditingUser(u)
    setEditDisplayName(u.display_name ?? '')
    setEditQuota(u.quota_bytes != null ? String(u.quota_bytes) : '')
    setEditActive(u.active)
    setDetailUser(null)
    setEditOpen(true)
  }

  const editMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Parameters<typeof usersApi.update>[1] }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('保存成功', '用户信息已更新')
      setEditOpen(false)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const handleEditSubmit = () => {
    if (!editingUser) return
    editMutation.mutate({
      id: editingUser.id,
      data: {
        display_name: editDisplayName.trim() || undefined,
        quota_bytes: editQuota.trim() ? Number(editQuota) : undefined,
        active: editActive,
      },
    })
  }

  // 多选 + 批量删除
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<User[]>([])

  const deleteMutation = useMutation({
    mutationFn: async (rows: User[]) => {
      await Promise.all(rows.map((u) => usersApi.delete(u.id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('删除成功', `已删除 ${selectedRows.length} 项`)
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // 详情
  const [detailUser, setDetailUser] = useState<User | null>(null)

  const columns: PagedColumn<User>[] = [
    {
      key: 'username',
      header: '用户名',
      headerTooltip: '登录邮箱的用户名部分，与域名组成完整邮箱地址',
      width: '180px',
      render: (u) => <Text weight="semibold">{u.username}</Text>,
    },
    {
      key: 'email',
      header: '邮箱',
      width: '240px',
      render: (u) => <span title={u.email}>{u.email}</span>,
    },
    {
      key: 'status',
      header: '状态',
      headerTooltip: '停用后该用户无法登录收件',
      width: '80px',
      render: (u) => (u.active ? '活跃' : '停用'),
    },
    {
      key: 'quota_bytes',
      header: '配额',
      headerTooltip: '该用户的邮箱容量上限，不限显示为不限',
      width: '100px',
      render: (u) => formatQuota(u.quota_bytes),
    },
    {
      key: 'created_at',
      header: '创建时间',
      width: '160px',
      render: (u) => formatDate(u.created_at),
    },
  ]

  return (
    <div className={styles.root}>
      <PagedGrid<User>
        rows={pageItems}
        columns={columns}
        loading={isLoading}
        rowKey={(u) => String(u.id)}
        total={filteredUsers.length}
        page={safePage}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyText={query ? '没有匹配的邮箱' : '该域名下暂无邮箱，点击左上角「新建邮箱」'}
        toolbarLeft={
          <>
            <Button appearance="primary" size="small" icon={<AddRegular />} onClick={openCreate}>
              新建邮箱
            </Button>
            <Switch
              checked={selectionMode === 'multiselect'}
              onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
              label="多选"
            />
          </>
        }
        selectionMode={selectionMode}
        onSelectionChange={setSelectedRows}
        onDelete={(rows) => deleteMutation.mutateAsync(rows)}
        onRowClick={(u) => setDetailUser(u)}
      />

      {/* 新建邮箱 */}
      <Dialog open={createOpen} onOpenChange={(_, d) => { if (!d.open) setCreateOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="新建邮箱" onClose={() => setCreateOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="新建邮箱所属的收件域，在左侧选中。">域名</InfoLabel>} required>
                  <Input value={domain.name} disabled />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="登录邮箱的用户名部分，与所选域名组成完整邮箱地址。">用户名</InfoLabel>} required>
                  <Input value={form.username} onChange={(_, d) => setForm((f) => ({ ...f, username: d.value }))} placeholder="请输入用户名" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="用户登录邮箱的密码。">密码</InfoLabel>} required>
                  <Input type="password" value={form.password} onChange={(_, d) => setForm((f) => ({ ...f, password: d.value }))} placeholder="请输入密码" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="用户的显示名称，用于客户端展示，可选。">显示名称</InfoLabel>}>
                  <Input value={form.display_name} onChange={(_, d) => setForm((f) => ({ ...f, display_name: d.value }))} placeholder="可选" />
                </Field>
                <Field className={`${styles.formGridItem} ${styles.colSpan2}`} label={<InfoLabel info="该用户的邮箱容量上限，留空表示不限。">配额（字节）</InfoLabel>} hint="留空表示不限">
                  <Input type="number" value={form.quota_bytes} onChange={(_, d) => setForm((f) => ({ ...f, quota_bytes: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleSubmit} disabled={createMutation.isPending}>
                {createMutation.isPending ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 编辑用户 */}
      <Dialog open={editOpen} onOpenChange={(_, d) => { if (!d.open) setEditOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="编辑用户" onClose={() => setEditOpen(false)} />
            <DialogContent>
              {editingUser && (
                <div className={styles.formGrid}>
                  <Field className={styles.formGridItem} label={<InfoLabel info="登录邮箱的用户名，不可修改。">用户名</InfoLabel>}>
                    <Input value={editingUser.username} disabled />
                  </Field>
                  <Field className={styles.formGridItem} label={<InfoLabel info="用户的显示名称，用于客户端展示。">显示名称</InfoLabel>}>
                    <Input value={editDisplayName} onChange={(_, d) => setEditDisplayName(d.value)} placeholder="可选" />
                  </Field>
                  <Field className={`${styles.formGridItem} ${styles.colSpan2}`} label={<InfoLabel info="该用户的邮箱容量上限，留空表示不限。">配额（字节）</InfoLabel>} hint="留空表示不限">
                    <Input type="number" value={editQuota} onChange={(_, d) => setEditQuota(d.value)} placeholder="可选" />
                  </Field>
                  <Field className={styles.formGridItem} label={<InfoLabel info="停用后该用户无法登录收件。">状态</InfoLabel>}>
                    <Switch checked={editActive} onChange={(_, d) => setEditActive(d.checked)} label={editActive ? '活跃' : '停用'} />
                  </Field>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleEditSubmit} disabled={editMutation.isPending}>
                {editMutation.isPending ? '保存中…' : '保存'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 详情 */}
      <Dialog open={!!detailUser} onOpenChange={(_, d) => !d.open && setDetailUser(null)} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="用户详情" onClose={() => setDetailUser(null)} />
            <DialogContent>
              {detailUser && (
                <DetailGrid key={detailUser.id}>
                  <DetailField label="ID" value={detailUser.id} mono />
                  <DetailField label="用户名" value={detailUser.username} mono />
                  <DetailField label="邮箱" value={detailUser.email} mono />
                  <DetailField label="显示名称" value={detailUser.display_name} />
                  <DetailField label="域名" value={detailUser.domain} mono />
                  <DetailField label="配额" value={detailUser.quota_bytes != null ? formatQuota(detailUser.quota_bytes) : '不限'} />
                  <DetailField label="管理员" value={detailUser.is_admin ? '是' : '否'} />
                  <DetailField label="状态" value={detailUser.active ? '活跃' : '停用'} />
                  <DetailField label="创建时间" value={detailUser.created_at} />
                </DetailGrid>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" icon={<EditRegular />} onClick={() => detailUser && openEdit(detailUser)}>
                编辑
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}